import type { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";
import type { AssetType } from "schemas/generated/asset";
import type { ShopifyAssetsGridProps } from "./ShopifyAssetsGrid";

import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { assetRouter } from "@editor/trpc/asset";

import {
  IMAGE_ACCEPT_FILE_EXTENSIONS,
  VIDEO_ACCEPT_FILE_EXTENSIONS,
} from "@/features/assets/constants";
import { skipToken } from "@tanstack/react-query";

import { AssetsGrid } from "../AssetsGrid";
import { EmptyState } from "./EmptyState";
import { NoSearchResults } from "./NoSearchResults";

type ReploAssetsGridProps = ShopifyAssetsGridProps & {
  shouldAllowActions?: boolean;
  getDropzoneRootProps?: (
    props?: DropzoneRootProps | undefined,
  ) => DropzoneRootProps;
  getDropzoneInputProps?: (
    props?: DropzoneInputProps | undefined,
  ) => DropzoneInputProps;
  droppedFilesMap: Map<string, File>;
  isDragActive?: boolean;
  className?: string;
};

const INITIAL_ASSETS_TO_LOAD = 16;

export const ReploAssetsGrid: React.FC<ReploAssetsGridProps> = ({
  getDropzoneInputProps,
  getDropzoneRootProps,
  assetTypes = ["image", "video"],
  searchTerm,
  onAssetSelected,
  shouldAllowActions,
  droppedFilesMap,
  isDragActive = false,
  className,
}) => {
  const projectId = useCurrentProjectId();

  const { data, isLoading, fetchNextPage, hasNextPage } =
    assetRouter.list.useInfiniteQuery(
      projectId
        ? {
            projectId,
            types: assetTypes,
            limit: INITIAL_ASSETS_TO_LOAD,
            search: searchTerm,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );

  // NOTE (Gabe 2025-02-26): We use this internal map so that assets loaded from
  // the server are removed. Otherwise they will reappear as loading if they are
  // deleted after being uploaded.
  const [internalDroppedFilesMap, setInternalDroppedFilesMap] = React.useState<
    Map<string, File>
  >(new Map());
  React.useEffect(() => {
    const newDroppedFilesMap = new Map(droppedFilesMap);
    setInternalDroppedFilesMap(newDroppedFilesMap);
  }, [droppedFilesMap]);
  React.useEffect(() => {
    setInternalDroppedFilesMap((val) => {
      const newMap = new Map(val);
      assets.forEach((asset) => {
        newMap.delete(asset.id);
      });
      return newMap;
    });
  }, [assets]);

  const uploadingAssets = React.useMemo(
    () =>
      Array.from(internalDroppedFilesMap.entries())
        .map(([id, file]) => ({
          id,
          name: file.name,
          url: URL.createObjectURL(file),
          type: file.type.split("/")[0] as AssetType,
          fileExtension: file.name.split(".").pop() ?? "",
          createdAt: new Date(),
          updatedAt: new Date(),
          sizeBytes: file.size,
          width: null,
          height: null,
          folderId: null,
          projectId: projectId ?? "",
          altText: null,
          embeddingText: null,
        }))
        .filter(({ id }) => !assets.some((a) => a.id === id)),
    [internalDroppedFilesMap, assets, projectId],
  );

  const dropDisabled = Boolean(searchTerm);

  const dropZoneRootProps = React.useMemo(() => {
    if (dropDisabled) {
      return undefined;
    }
    return getDropzoneRootProps?.({
      onClick: (event) => {
        if (assets.length > 0 || (uploadingAssets?.length ?? 0) > 0) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
    });
  }, [getDropzoneRootProps, dropDisabled, assets, uploadingAssets]);

  const dropZoneInputProps = React.useMemo(() => {
    if (dropDisabled) {
      return undefined;
    }
    return getDropzoneInputProps?.();
  }, [getDropzoneInputProps, dropDisabled]);

  return (
    <div {...dropZoneRootProps} className="contents">
      {dropZoneInputProps && <input {...dropZoneInputProps} />}
      <AssetsGrid.Root
        shouldAllowActions={shouldAllowActions}
        assets={assets}
        droppedAssets={uploadingAssets}
        isLoading={isLoading}
        isDragActive={isDragActive}
        onAssetSelected={onAssetSelected}
        hasNextPage={hasNextPage}
        fetchNextPage={() => void fetchNextPage()}
        searchTerm={searchTerm}
        emptyState={
          searchTerm ? (
            <NoSearchResults searchTerm={searchTerm} />
          ) : (
            <ReploAssetEmptyState />
          )
        }
        className={className}
      >
        <AssetsGrid.ListDetailed />
      </AssetsGrid.Root>
    </div>
  );
};

const ReploAssetEmptyState: React.FC = () => {
  const fileExtensions = [
    ...IMAGE_ACCEPT_FILE_EXTENSIONS,
    ...VIDEO_ACCEPT_FILE_EXTENSIONS,
  ];
  return (
    <EmptyState>
      <div className="header-h5">Click to upload</div>
      <div className="text-sm">
        {" "}
        or drag and drop{" "}
        {fileExtensions
          .map((val) => val.replace(".", "").toUpperCase())
          .join(", ")}
      </div>
    </EmptyState>
  );
};
